import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import type { AdvisorySourceReferenceV01 } from './advisoryProvenance';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';
import type { AdvisoryTableSourceReference } from './advisoryTableSources';
import { AdvisoryQuoteLabel } from './AdvisoryQuoteLabel';
import { AdvisoryDisclosure } from './AdvisoryDisclosure';
import { AdvisoryChapterLink } from './AdvisoryChapterLink';
import { AdvisorySourceMetadata } from './AdvisorySourceMetadata';
import { advisorySourceCheck } from './advisoryReportEvidence';
import { SourceQuotes } from './AdvisorySourceQuotes';
import {
  sourcePassageRequest,
  type AdvisorySourcePassageLoader,
  type AdvisorySourcePassageResult,
} from './advisorySourcePassage';

export interface AdvisorySourceUse {
  answerKey: string;
  label: string;
  chapter?: number;
}

export function AdvisorySourceDetails({
  source,
  quoteReferences,
  tableReferences = [],
  quoteIndex,
  headingRef,
  uses,
  onOpenAnswer,
  loadSourcePassage,
}: {
  source: AdvisorySourceReferenceV01;
  quoteReferences?: readonly AdvisoryQuoteReference[];
  tableReferences?: readonly AdvisoryTableSourceReference[];
  quoteIndex?: number;
  headingRef: React.Ref<HTMLHeadingElement>;
  uses: readonly AdvisorySourceUse[];
  onOpenAnswer?: (answerKey: string) => void;
  loadSourcePassage?: AdvisorySourcePassageLoader;
}) {
  const { t } = useUntypedTranslation();
  const [usesExpanded, setUsesExpanded] = useState(true);
  const [passageExpanded, setPassageExpanded] = useState(false);
  const [passage, setPassage] = useState<
    AdvisorySourcePassageResult | { status: 'loading' } | null
  >(null);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  const request = sourcePassageRequest(source);
  const readPassage = async () => {
    if (!request || !loadSourcePassage) return;
    controller.current?.abort();
    const next = new AbortController();
    controller.current = next;
    setPassage({ status: 'loading' });
    try {
      const result = await loadSourcePassage(request, next.signal);
      if (!next.signal.aborted) setPassage(result);
    } catch {
      if (!next.signal.aborted) setPassage({ status: 'failed' });
    }
  };
  const togglePassage = () => {
    if (passageExpanded) {
      if (passage?.status === 'loading') {
        controller.current?.abort();
        setPassage(null);
      }
      setPassageExpanded(false);
    } else {
      setPassageExpanded(true);
      if (!passage) void readPassage();
    }
  };
  const selectedReferences = (quoteReferences ?? []).filter(
    reference => quoteIndex === undefined || reference.quoteIndex === quoteIndex
  );
  const checkStatus =
    quoteIndex === undefined ? advisorySourceCheck(source) : source.quotes?.[quoteIndex]?.status;
  return (
    <Box data-testid="advisory-source-details" sx={{ display: 'grid', gap: 2.5 }}>
      <Box
        sx={theme => ({
          borderBottom: `1px solid ${theme.visualPrimitives.borderLight}`,
          paddingBottom: 2,
        })}
      >
        {tableReferences.length ? (
          <Box sx={{ display: 'flex', gap: 1, marginBottom: 1 }}>
            {tableReferences.map(reference => (
              <AdvisoryQuoteLabel key={reference.label} label={reference.label} />
            ))}
            <Typography sx={{ fontSize: 12 }}>{t('advisory:brief.sources.tableSource')}</Typography>
          </Box>
        ) : null}
        <Typography
          component="h2"
          id="advisory-sources-title"
          ref={headingRef}
          tabIndex={-1}
          sx={{ fontSize: 20, fontWeight: 500, lineHeight: 1.4, overflowWrap: 'anywhere' }}
        >
          {source.title ?? t('advisory:brief.sources.untitled')}
        </Typography>
        <AdvisorySourceMetadata
          source={source}
          checkStatus={tableReferences.length && quoteIndex === undefined ? undefined : checkStatus}
          quoteReferences={selectedReferences}
          currentQuoteIndex={quoteIndex}
        />
      </Box>
      <SourceQuotes
        source={source}
        quoteReferences={selectedReferences.length > 1 ? quoteReferences : undefined}
        quoteIndex={quoteIndex}
        initiallyExpanded
      />
      {quoteIndex === undefined ? (
        <AdvisoryDisclosure
          title={t('advisory:brief.sources.usedIn')}
          expanded={usesExpanded}
          onToggle={() => setUsesExpanded(value => !value)}
        >
          <Box component="ul" sx={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {uses.map(use => (
              <Box component="li" key={use.answerKey}>
                <AdvisoryChapterLink
                  label={use.label}
                  chapter={use.chapter}
                  onClick={onOpenAnswer ? () => onOpenAnswer(use.answerKey) : undefined}
                />
              </Box>
            ))}
          </Box>
        </AdvisoryDisclosure>
      ) : null}
      <Box
        sx={theme => ({
          borderTop: `1px solid ${theme.visualPrimitives.borderLight}`,
          paddingTop: 2,
        })}
      >
        {request && loadSourcePassage ? (
          <AdvisoryDisclosure
            title={t('advisory:brief.sources.passage.title')}
            expanded={passageExpanded}
            onToggle={togglePassage}
          >
            {passage ? (
              <Box role="status" data-passage-status={passage.status}>
                {passage.status === 'ready' ? (
                  <>
                    <Typography sx={{ fontSize: 12 }}>
                      {t('advisory:brief.sources.passage.hint')}
                    </Typography>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: 15,
                        lineHeight: 1.65,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        marginTop: 2,
                      }}
                    >
                      {passage.text}
                    </Typography>
                  </>
                ) : (
                  <Typography sx={{ fontSize: 13 }}>
                    {t(`advisory:brief.sources.passage.${passage.status}`)}
                  </Typography>
                )}
              </Box>
            ) : null}
            {passage && passage.status !== 'ready' && passage.status !== 'loading' ? (
              <Button variant="outlined" size="small" onClick={readPassage} sx={{ marginTop: 1 }}>
                {t('advisory:brief.sources.passage.retry')}
              </Button>
            ) : null}
          </AdvisoryDisclosure>
        ) : source.origin !== 'computed' ? (
          <Typography sx={theme => ({ fontSize: 13, color: theme.colors.inkTertiary })}>
            {t(
              request
                ? 'advisory:brief.sources.passage.unavailable'
                : 'advisory:brief.sources.passage.noReference'
            )}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
